import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

function formatShortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</p>
      <p className={`text-2xl font-bold ${accent || 'text-gray-900'}`}>{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

export default function SafetyNetOverview({ stats, loading, error }) {
  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500 mb-8">
        Loading SafetyNet overview…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center text-amber-900 mb-8">
        <p className="font-semibold mb-1">Couldn&apos;t load SafetyNet overview</p>
        <p className="text-sm text-amber-800">{error}</p>
      </div>
    );
  }

  if (!stats) return null;

  const scanChartData = (stats.scansByDay || []).map((row) => ({
    ...row,
    label: formatShortDate(row.date),
    total: (row.imageScans || 0) + (row.termScans || 0),
  }));

  const alertBreakdown = [
    { name: 'Total alerts', value: stats.totalAlerts || 0 },
    { name: 'Last 7 days', value: stats.alertsLast7Days || 0 },
    { name: 'High priority', value: stats.highPriorityAlerts || 0 },
  ];

  return (
    <section className="mb-10 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h2 className="text-xl font-bold text-gray-900">SafetyNet overview</h2>
          <p className="text-sm text-gray-500">Monitor uptime, scan activity, and recent detections.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Monitored artworks" value={stats.monitoredArtworks ?? 0} sub="Verified works under active watch" />
        <StatCard label="Open alerts" value={stats.totalAlerts ?? 0} sub={`${stats.alertsLast7Days ?? 0} in the last 7 days`} accent="text-ato-red" />
        <StatCard label="High-priority alerts" value={stats.highPriorityAlerts ?? 0} sub="Strong image match or term hit" accent="text-ato-red" />
        <StatCard label="Monitor uptime" value={`${(stats.uptimePercent ?? 0).toFixed(1)}%`} sub="Based on recent scan activity" accent={stats.monitorOnline ? 'text-green-600' : 'text-amber-600'} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4">Scan activity (14 days)</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={scanChartData}>
                <defs>
                  <linearGradient id="scanGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#E02829" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#E02829" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area type="monotone" dataKey="total" name="Scans" stroke="#E02829" fill="url(#scanGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4">Alert summary</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={alertBreakdown}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" name="Alerts" fill="#E02829" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {(stats.recentDetections?.length > 0) && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm overflow-hidden">
          <h3 className="font-semibold text-gray-900 mb-4">Recent detections</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="pb-2 font-medium">Artwork</th>
                  <th className="pb-2 font-medium">Source</th>
                  <th className="pb-2 font-medium">Type</th>
                  <th className="pb-2 font-medium text-right">Match</th>
                  <th className="pb-2 font-medium text-right">Detected</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentDetections.map((row) => (
                  <tr key={row.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 pr-4 font-medium text-gray-900">{row.artworkName}</td>
                    <td className="py-3 pr-4 text-gray-600 max-w-[200px] truncate">{row.domain}</td>
                    <td className="py-3 pr-4 capitalize text-gray-600">{row.type}</td>
                    <td className="py-3 pr-4 text-right text-gray-600">
                      {row.score != null ? `${Math.round(row.score)}%` : '—'}
                    </td>
                    <td className="py-3 text-right text-gray-500">{formatShortDate(row.detectedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}